import {
    isUndef,
    dateNow,
    dateParse,
} from '@/lib/utils';

// 是否支持存储数据
const supportLocalStorage = !isUndef(window.localStorage);
export const isSupport = supportLocalStorage;
const localStorage = window.localStorage; // tip: 为了单元测试能够正常运行

/**
 * 判断是否过期
 *
 * @param {String} time 日期
 *
 * @return {} 是否已经过期
 */
function isExpire(time: string) {
    return dateParse(time).valueOf() < dateNow().valueOf();
}

/**
 * 获得数据
 *
 * @param {} key
 *
 * @return {} 结果内容
 */
export function get(key: string) {
    if (!supportLocalStorage) {
        return null;
    }
    // 判断是否存在expire
    const exp = getExpire(key);
    if (!isUndef(exp) && isExpire(exp!)) {
        // 如果发现过期数据，那么删除数据和过期时间
        del(key);
        del('_e_' + key);
    }
    const result = localStorage.getItem(key);
    return result;
}

/**
 * 获得expire时间
 *
 * @param {} key
 * @return {string | null}
 */
export function getExpire(key: string): string | null {
    if (!supportLocalStorage) {
        return null;
    }
    return localStorage.getItem('_e_' + key);
}

/**
 * 设置数据
 *
 * @param {} key
 * @param {} value 允许string/number/object，对object将进行JSON.stringify
 */
export function set(key: string, value: string | number | object) {
    if (!supportLocalStorage) {
        return;
    }
    let result: string = '';
    if (typeof value === 'string') {
        result = value;
    } else if (typeof value === 'number') {
        result = value + '';
    } else if (typeof value === 'object') {
        result = JSON.stringify(value);
    }
    localStorage.setItem(key, result);
}

/**
 * 标记过期数据
 *
 * @param {} key
 * @param {} expire 过期时间
 */
export function expire(key: string, exp: string) {
    if (!supportLocalStorage) {
        return;
    }
    set('_e_' + key, exp);
}

/**
 * 设置数据，包括expire
 *
 * @param {string} key
 * @param {any} value
 * @param {} exp 过期时间,例如2019-01-01
 */
export function setex(key: string, value: string | number | object, exp: string) {
    if (!supportLocalStorage) {
        return;
    }
    set(key, value);
    if (exp) {
        expire(key, exp);
    }
}

/**
 * 删除数据
 */
export function del(key: string) {
    if (!supportLocalStorage) {
        return;
    }
    localStorage.removeItem(key);
}

/**
 * 支持普通的key也支持path
 * @param {} exp 过期时间,例如2019-01-01
 */
export function pathSet(keyOrPath: string, value: string | number | object, exp?: string) {
    if (!supportLocalStorage) {
        return;
    }
    const parts = keyOrPath.split('.');
    if (parts.length === 1) {
        if (exp) {
            setex(keyOrPath, value, exp);
        } else {
            set(keyOrPath, value);
        }
    } else {
        const first = parts.shift()!;
        const last = parts.pop()!;
        const storeValue = get(first) || '{}';
        const obj = JSON.parse(storeValue);
        let traverse = obj;
        for (const part of parts) {
            // 寻找失败
            if (!traverse[part]) {
                traverse[part] = {};
            }
            if (typeof traverse[part] !== 'object') {
                return;
            }
            traverse = traverse[part];
        }
        traverse[last] = value;
        if (exp) {
            setex(first, obj, exp);
        } else {
            set(first, obj);
        }
    }
}

export function pathDel(keyOrPath: string) {
    if (!supportLocalStorage) {
        return;
    }
    const parts = keyOrPath.split('.');
    if (parts.length === 1) {
        del(keyOrPath);
    } else {
        const first = parts.shift()!;
        const last = parts.pop()!;
        const storeValue = get(first) || '{}';
        const obj = JSON.parse(storeValue);
        let traverse = obj;
        for (const part of parts) {
            // 寻找失败
            if (!traverse[part]) {
                traverse[part] = {};
            }
            if (typeof traverse[part] !== 'object') {
                return;
            }
            traverse = traverse[part];
        }
        delete traverse[last];
        const exp = getExpire(first);
        if (exp) {
            setex(first, obj, exp);
        } else {
            set(first, obj);
        }
    }
}

export function pathGet(keyOrPath: string) {
    if (!supportLocalStorage) {
        return;
    }
    const parts = keyOrPath.split('.');
    if (parts.length === 1) {
        return get(keyOrPath);
    } else {
        const first = parts.shift()!;
        const last = parts.pop()!;
        const storeValue = get(first) || '{}';
        let traverse = JSON.parse(storeValue);
        for (const part of parts) {
            traverse = traverse[part];
            // 寻找失败
            if (!traverse || typeof traverse !== 'object') {
                return;
            }
        }
        return traverse[last];
    }
}

/**
 * 获得sessionStorage的数据
 *
 * @param {} key
 *
 * @return {} 结果内容
 */
export function getSessionStorage(key: string) {
    const result = sessionStorage.getItem(key);
    return result;
}

/**
 * 设置sessionStorage数据
 *
 * @param {} key
 * @param {} value
 */
export function setSessionStorage(key: string, value: string) {
    sessionStorage.setItem(key, value);
}
/**
 * 删除sessionStorage的数据
 *
 * @param {} key
 *
 * @return {} 结果内容
 */
export function delSessionStorage(key: string) {
    const result = sessionStorage.removeItem(key);
    return result;
}
